import { ChangeDetectorRef, Component, Input, OnChanges, OnInit } from '@angular/core';
import { MenuService } from '../menu/service/menu.service';
import { TranslateService } from '@ngx-translate/core';
import { DropdownOption } from '../../models/dropdown-option';
import { forkJoin, map } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { Router } from '@angular/router';
import { CurrentView } from '../../../api/models/user';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  standalone: false,
})
export class HeaderComponent implements OnInit, OnChanges {
  @Input() view!: CurrentView;
  selectedLanguage: DropdownOption | undefined;
  languageOptions: DropdownOption[] = [];

  constructor(
    private menuService: MenuService,
    public translate: TranslateService,
    public authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef

  ) {}

  shouldDisplayButtons = this.checkIfShouldDisplayButtons();

  ngOnInit(): void {
    const defaultLanguage = 'en';
    this.translate.setDefaultLang(defaultLanguage);
    this.translate.use(this.translate.currentLang || defaultLanguage);

    this.setLanguageOptions().subscribe(() => {
      this.selectedLanguage = this.languageOptions.find(
        (l) => l.value === this.translate.currentLang,
      );
      this.cdr.detectChanges();
    });

    this.translate.onLangChange.subscribe(() => {
      this.setLanguageOptions().subscribe(() => {
        this.selectedLanguage = this.languageOptions.find(
          (l) => l.value === this.translate.currentLang,
        )
      })
    });

    this.router.events.subscribe(() => {
      this.shouldDisplayButtons = this.checkIfShouldDisplayButtons();
    });
  }

  ngOnChanges(): void {
    this.setLanguageOptions().subscribe();
    this.updateLanguageForView(this.view)
    this.cdr.detectChanges();
  }

  updateLanguageForView(view: CurrentView) {
    const isCurrentLanguagePt = this.selectedLanguage?.value === 'pt'
    if (isCurrentLanguagePt) {
      const defaultLanguage = view === CurrentView.BR ? 'pt' : 'es';
      if (!this.languageOptions.some(option => option.value === this.translate.currentLang)) {
        this.translate.use(defaultLanguage);
        this.selectedLanguage = this.languageOptions.find(
          (l) => l.value === this.translate.currentLang,
        );
        this.cdr.detectChanges();
      }
    }
  }

  checkIfShouldDisplayButtons() {
    return (
      this.authService.isAuthenticated() && this.router.url !== '/access-denied'
    );
  }

  toggleMenu() {
    this.menuService.toggleMenu();
  }

  setLanguageOptions() {
    return this.getLanguageOptions().pipe(
      map((options) => {
        this.languageOptions = options;
        return options;
      }),
    );
  }

  getLanguageOptions() {
    let availableLanguages = this.translate.getLangs();

    if (this.view === CurrentView.BR) {
      availableLanguages = ['en', 'pt', 'es'];
    } else if (this.view === CurrentView.LATAM) {
      availableLanguages = ['en', 'es'];
    }

    return forkJoin(
      availableLanguages.map((locale) =>
        this.translate.get(`LANGUAGES.${locale}`).pipe(
          map((translation) => {
            return {
              label: translation,
              value: locale,
              colorStyle: '',
            };
          }),
        ),
      ),
    );
  }

  didChangeLanguage(option: DropdownOption) {
    const language = option.value as 'pt' | 'en' | 'es';
    this.translate.use(language);
  }

  updateDropdownLanguageText(selectedOption: DropdownOption) {
    return (selectedOption.value as string).toUpperCase();
  }
}
